import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiSend, FiCheckCircle, FiLoader, FiHeart } from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

/**
 * Growth Plan §1.1 companion — public feedback capture.
 *
 * Users arrive here from a broadcast email link like:
 *   https://snap-chart.ai/feedback?e=<email>&t=<hmac>&s=<campaign_id>
 *
 * The HMAC token is generated by the send-broadcast-email Edge Function
 * using the same UNSUBSCRIBE_SECRET the DB RPC verifies against — random
 * internet visitors without a valid token get logged as 'unverified'
 * (or blocked if the secret is configured). No login required for the
 * user, which is critical for a dormant-user winback flow: forcing sign
 * in kills the response rate.
 */

const FeedbackForm = () => {
  const [params] = useSearchParams();
  const emailFromLink = params.get('e') || '';
  const tokenFromLink = params.get('t') || '';
  const source        = params.get('s') || 'why-did-you-leave';

  const [email, setEmail] = useState(emailFromLink);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (emailFromLink) setEmail(emailFromLink);
  }, [emailFromLink]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !message.trim()) return;
    setSending(true);
    setError(null);
    try {
      const { data, error: rpcErr } = await supabase.rpc('submit_growth_feedback', {
        p_email:      email.trim().toLowerCase(),
        p_token:      tokenFromLink,
        p_message:    message.trim(),
        p_source:     source,
        p_first_name: null,
        p_ip:         null,
        p_user_agent: navigator.userAgent.slice(0, 200),
      });
      if (rpcErr) throw rpcErr;
      if (!data?.success) throw new Error(data?.error || 'Submission failed.');
      setDone(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 relative overflow-hidden">
      {/* Grid backdrop */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:48px_48px]" />
      <div className="pointer-events-none absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-cyan-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-purple-500/10 blur-3xl" />

      <div className="relative max-w-xl mx-auto px-4 py-16 sm:py-24">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-4 hover:text-cyan-300">
            ← snap-chart.ai
          </Link>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-purple-400 mb-3">
            ▸ Feedback
          </div>
        </div>

        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl border border-green-500/30 bg-gradient-to-br from-green-500/10 via-gray-900/70 to-cyan-500/10 p-8 sm:p-10 text-center"
          >
            <div className="w-16 h-16 mx-auto rounded-full border border-green-500/40 bg-green-500/10 flex items-center justify-center mb-4">
              <FiCheckCircle className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-3xl font-black text-white mb-2">Thank you.</h1>
            <p className="text-gray-300 mb-6">
              I read every reply myself. If you left your email above I&apos;ll follow up personally about your <strong className="text-cyan-400">3 free months of Pro</strong>.
            </p>
            <p className="text-xs text-gray-500 font-mono uppercase tracking-widest">
              <FiHeart className="w-3 h-3 inline text-red-400 mr-1" /> — JM, founder
            </p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl border-2 border-cyan-400/30 bg-gray-900/70 backdrop-blur-xl p-8 sm:p-10 shadow-2xl shadow-cyan-500/10"
          >
            <h1 className="text-3xl sm:text-4xl font-black text-white mb-2 leading-tight">
              What did we get wrong?
            </h1>
            <p className="text-gray-400 mb-6">
              One line is enough. What was missing, confusing, or why didn&apos;t you come back? I read every reply personally.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">
                  Your email
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-cyan-400/50"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">
                  Your one line (or paragraph — up to you)
                </label>
                <textarea
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, 4000))}
                  rows={5}
                  maxLength={4000}
                  placeholder="e.g. 'I needed backtesting.' · 'The verdict felt slow.' · 'I forgot it existed.'"
                  className="w-full bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400/50 resize-y"
                />
                <div className="text-[10px] font-mono text-gray-600 mt-1 text-right">
                  {message.length} / 4000
                </div>
              </div>

              {error && (
                <div className="px-4 py-2.5 rounded-lg border border-red-500/30 bg-red-500/5 text-red-300 font-mono text-xs">
                  ✗ {error}
                </div>
              )}

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={sending || !email || !message.trim()}
                  className="w-full inline-flex justify-center items-center gap-2 px-5 py-3.5 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black text-base font-bold shadow-lg shadow-cyan-500/30 hover:scale-[1.01] transition-transform disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {sending ? (
                    <><FiLoader className="w-4 h-4 animate-spin" /> Sending…</>
                  ) : (
                    <><FiSend className="w-4 h-4" /> Send feedback</>
                  )}
                </button>
                <p className="mt-3 text-[10px] font-mono uppercase tracking-widest text-gray-600 text-center">
                  30-sec form · no spam · zero follow-ups unless you want them
                </p>
              </div>
            </form>
          </motion.div>
        )}

        <div className="mt-8 text-center text-xs text-gray-600 font-mono">
          Snap Chart AI · The AI that tells you when NOT to trade
        </div>
      </div>
    </div>
  );
};

export default FeedbackForm;
